import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
} from "@mui/material";
import { iconColors } from "../utils/statusColors";
import PAPIcon from "../assets/PAP.svg";
import InternationalSourcingIcon from "../assets/IntSourcing.svg";
import CopayAssistIcon from "../assets/CoPayAssist.svg";
import ECMPIcon from "../assets/ECMP.svg";
import GLPIcon from "../assets/GLP 1.svg";
import BiosimilarsIcon from "../assets/Biosimilars.svg";
import WomensHealthIcon from "../assets/WomensHealth.svg";
import DiabetesIcon from "../assets/Diabetes.svg";
import WeightManagementIcon from "../assets/WeightManagement.svg";
const getSvgFilter = (color) => {
  const hexToRgb = (hex) => {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  const [r, g, b] = hexToRgb(color);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='recolor' color-interpolation-filters='sRGB'%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 ${
    r / 255
  } 0 0 0 0 ${g / 255} 0 0 0 0 ${
    b / 255
  } 0 0 0 1 0'/%3E%3C/filter%3E%3C/svg%3E#recolor")`;
};

const getProgramIcon = (programName) => {
  const style = {
    width: "16px",
    height: "16px",
    marginRight: "4px",
    filter: getSvgFilter(iconColors?.iconColor),
  };

  if (!programName) return null;

  const program = programName.toLowerCase();

  if (program.includes("pap")) {
    return <img src={PAPIcon} alt="PAP" style={style} />;
  } else if (
    program.includes("international") ||
    program.includes("sourcing")
  ) {
    return (
      <img
        src={InternationalSourcingIcon}
        alt="International Sourcing"
        style={style}
      />
    );
  } else if (program.includes("copay") || program.includes("assist")) {
    return <img src={CopayAssistIcon} alt="Copay Assist" style={style} />;
  } else if (program.includes("ecmp")) {
    return <img src={ECMPIcon} alt="ECMP" style={style} />;
  } else if (program.includes("glp") || program.includes("1")) {
    return <img src={GLPIcon} alt="GLP-1" style={style} />;
  } else if (program.includes("biosimilar")) {
    return <img src={BiosimilarsIcon} alt="Biosimilar" style={style} />;
  } else if (program.includes("women")) {
    return <img src={WomensHealthIcon} alt="Women's Health Program" style={style} />;
  } else if (program.includes("diabete")) {
    return <img src={DiabetesIcon} alt="Diabetes" style={style} />;
  } else if (program.includes("weight")) {
    return <img src={WeightManagementIcon} alt="Weight Management" style={style} />;
  }

  return null;
};

const extractValueFromPart = (part, prefix) => {
  const prefixIndex = part.indexOf(prefix) + prefix.length;
  return part.substring(prefixIndex).trim();
};

const parseMemberFormat = (trimmedLine) => {
  const parts = trimmedLine.split(",").map((part) => part.trim());
  const memberPart = parts.find((part) => part.includes("Member:"));
  const outcomePart = parts.find((part) => part.includes("Outcome:"));
  const reasonPart = parts.find((part) => part.includes("Reason:"));

  if (!memberPart || !outcomePart) {
    return null;
  }

  const memberName = extractValueFromPart(memberPart, "Member:");
  const outcome = extractValueFromPart(outcomePart, "Outcome:");
  const reason = reasonPart ? extractValueFromPart(reasonPart, "Reason:") : "";

  if (!memberName || !outcome) {
    return null;
  }

  return {
    program: memberName,
    status: "",
    outcome: outcome,
    reason: reason,
    isMemberFormat: true,
  };
};

const parseProgramFormat = (trimmedLine) => {
  const parts = trimmedLine.split(",").map((part) => part.trim());
  const programPart = parts.find((part) => part.includes("Program:"));
  const statusPart = parts.find((part) => part.includes("Status:"));
  const outcomePart = parts.find((part) => part.includes("Outcome:"));
  const reasonPart = parts.find((part) => part.includes("Reason:"));

  if (!programPart || !statusPart || !outcomePart) {
    return null;
  }

  const programName = extractValueFromPart(programPart, "Program:");
  const status = extractValueFromPart(statusPart, "Status:");
  const outcome = extractValueFromPart(outcomePart, "Outcome:");
  const reason = reasonPart ? extractValueFromPart(reasonPart, "Reason:") : "";

  if (!programName || !status || !outcome) {
    return null;
  }

  return {
    program: programName,
    status: status,
    outcome: outcome,
    reason: reason,
    isMemberFormat: false,
  };
};

const parseMessageLine = (line) => {
  // Safety check: limit input length to prevent excessive processing and ReDoS attacks
  if (line.length > 1000) {
    return null;
  }

  const trimmedLine = line.trim();

  // Check for member format
  if (trimmedLine.includes("Member:") && trimmedLine.includes("Outcome:")) {
    return parseMemberFormat(trimmedLine);
  }

  // Check for program format
  if (
    trimmedLine.includes("Program:") &&
    trimmedLine.includes("Status:") &&
    trimmedLine.includes("Outcome:")
  ) {
    return parseProgramFormat(trimmedLine);
  }

  return null;
};

const getOutcomeColor = (outcome, outcomeColors) => {
  return outcomeColors[outcome];
};

export default function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText = "Go back",
  confirmButtonColor = "primary",
  icon,
  outcomeColors,
}) {
  const tableRows =
    typeof message === "string"
      ? message.split("\n").map(parseMessageLine).filter(Boolean)
      : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "10px",
            width: "auto",
            minWidth: { xs: "90%", sm: "520px" },
            height: "400px",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {icon && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
            backgroundColor: "#45B1841A",
            borderRadius: "50%",
            width: 56,
            height: 56,
            mx: "auto",
            position: "relative",
            zIndex: 2,
            flexShrink: 0,
            flexGrow: 0,
            flexBasis: 56,
            minWidth: 56,
            minHeight: 56,
            maxWidth: 56,
            maxHeight: 56,
          }}
        >
          {icon}
        </Box>
      )}
      <DialogTitle
        sx={{ textAlign: "center", color: "#1B2F3F", fontWeight: 600 }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ overflowY: "hidden" }}>
        {tableRows.length > 0 ? (
          <Box
            sx={{
              maxHeight: 180,
              overflowY: "auto",
              overflowX: "hidden",
              mt: 1,
            }}
          >
            <Table size="small" sx={{ minWidth: 400 }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#CFCEDA",
                    "& .MuiTableCell-head": {
                      backgroundColor: "#CFCEDA",
                      color: "#403C6F",
                    },
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "#403C6F",
                      backgroundColor: "#CFCEDA",
                      borderRight: "1px solid #ffff",
                      padding: "8px 16px",
                      minWidth: "90px",
                      borderTopLeftRadius: "8px",
                    }}
                  >
                    {tableRows[0]?.isMemberFormat ? "Member" : "Program"}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "#403C6F",
                      backgroundColor: "#CFCEDA",
                      padding: "8px 16px",
                      minWidth: "auto",
                      borderTopRightRadius: "8px",
                    }}
                  >
                    Outreach Outcome
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row, idx) => (
                  <TableRow
                    key={`confirmation-row-${row.program || ""}-${row.outcome || ""}-${idx}`}
                    sx={{
                      "& td": {
                        borderBottom: "1px solid #E8E8E9",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        borderBottom: "none",
                        padding: "8px 8px 8px 16px",
                        verticalAlign: "middle",
                        borderLeft: "1px solid #E8E8E9",
                      }}
                    >
                      {row.isMemberFormat ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            height: "32px",
                            boxSizing: "border-box",
                            padding: 0,
                            marginLeft: 0,
                          }}
                        >
                          <span
                            style={{
                              color: "#403C6F",
                              fontWeight: 700,
                              fontSize: "12px",
                              lineHeight: "150%",
                              letterSpacing: "0.2px",
                              whiteSpace: "nowrap",
                              padding: 0,
                              margin: 0,
                            }}
                          >
                            {row.program || "N/A"}
                          </span>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "inline-flex",
                            gap: 0,
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "16px",
                            p: "4px 8px",
                            height: "32px",
                            boxSizing: "border-box",
                            marginRight: "2px",
                            bgcolor: iconColors.bgColor,
                            border: `1px solid ${iconColors.borderColor}`,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {getProgramIcon(row.program)}
                            <span
                              style={{
                                color: iconColors?.color,
                                fontWeight: 700,
                                fontSize: "12px",
                                lineHeight: "150%",
                                letterSpacing: "0%",
                                paddingLeft: 4,
                                paddingRight: 2,
                              }}
                            >
                              {row.program || "N/A"}
                            </span>
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: "none",
                        borderRight: "1px solid #E8E8E9",
                        padding: "8px 16px",
                        verticalAlign: "middle",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <span
                          style={{
                            color: getOutcomeColor(row.outcome, outcomeColors),
                            fontWeight: 600,
                            fontSize: "12px",
                            lineHeight: "150%",
                            letterSpacing: "0.2px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.outcome}
                        </span>
                        {row.reason && (
                          <span
                            style={{
                              fontWeight: 400,
                              fontSize: "12px",
                              color: "#403C6F",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {` (${row.reason})`}
                          </span>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <p style={{ margin: 0, color: "#666", textAlign: "center" }}>
            {message}
          </p>
        )}
      </DialogContent>
      <DialogActions
        style={{
          padding: "16px 24px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "30px",
            backgroundColor: "transparent",
            border: "1px solid #CFCEDA",
            color: "#403C6F",
            textTransform: "none",
            width: "187px",
            height: "42px",
            "&:hover": {
              backgroundColor: "rgba(207, 206, 218, 0.1)",
              border: "1px solid #CFCEDA",
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            borderRadius: "30px",
            textTransform: "none",
            width: "187px",
            height: "42px",
            backgroundColor: "#45B184",
            "&:hover": {
              backgroundColor: "#36996a",
            },
          }}
          color={
            confirmButtonColor === "error" ? undefined : confirmButtonColor
          }
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmButtonColor: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([
      "primary",
      "secondary",
      "error",
      "info",
      "success",
      "warning",
    ]),
  ]),
  icon: PropTypes.node,
  outcomeColors: PropTypes.object,
};

ConfirmationDialog.defaultProps = {
  cancelText: "Go back",
  confirmButtonColor: "primary",
};
